import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { MsalHelperService } from './msal-helper.service';
import { ContextService } from './context.service';

export interface StartActivationResult {
    ok: boolean;
    alreadyVerified?: boolean;
    contactEmail?: string;
    contactEmailPending?: string;
    expiresAt?: string;
    error?: string;
    retryAfterSec?: number;
}

export interface VerifyResult {
    ok: boolean;
    contactEmail?: string;
    reason?: string;
}

@Injectable({ providedIn: 'root' })
export class UserProfileService {
    contactEmail$ = new BehaviorSubject<string | null>(null);
    contactEmailVerified$ = new BehaviorSubject<boolean>(false);
    contactEmailPending$ = new BehaviorSubject<string | null>(null);
    /** True once context has been fetched (success or failure). The gate uses
     *  this to avoid flashing before we know whether the user is verified. */
    contextChecked$ = new BehaviorSubject<boolean>(false);

    constructor(
        private msalHelperService: MsalHelperService,
        private contextService: ContextService,
    ) {
        this.contextService.clarityUser$.subscribe((u: any) => {
            if (u === null) return; // BehaviorSubject seed — wait for first real emission
            this.contactEmail$.next((u?.contactEmail as string) ?? null);
            this.contactEmailVerified$.next(u?.contactEmailVerified === true);
            this.contactEmailPending$.next((u?.contactEmailPending as string) ?? null);
            this.contextChecked$.next(true);
        });
    }

    /** Trigger an initial context load if it hasn't run yet. Idempotent. */
    async ensureLoaded(): Promise<void> {
        try { await this.contextService.load(); } catch { /* no-op */ }
        finally { this.contextChecked$.next(true); }
    }

    /** Re-fetch /api/context so the gate flips to verified after the link is clicked. */
    async refresh(): Promise<void> {
        await this.contextService.reload();
    }

    async requestActivation(contactEmail: string): Promise<StartActivationResult> {
        const result = await firstValueFrom(
            this.msalHelperService.getAccessTokenSilent$([
                'https://service.flow.microsoft.com//Flows.Manage.All',
            ])
        ).catch(() => null);
        if (!result?.accessToken) return { ok: false, error: 'not_authenticated' };

        const res = await fetch('/api/start-contact-email', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${result.accessToken}`,
                'x-authorization': `Bearer ${result.accessToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ contactEmail }),
        }).catch(() => null);
        if (!res) return { ok: false, error: 'network_error' };
        const data: Record<string, unknown> = await res.json().catch(() => ({}));
        if (!res.ok) {
            return {
                ok: false,
                error: typeof data['error'] === 'string' ? (data['error'] as string) : `http_${res.status}`,
                retryAfterSec: typeof data['retryAfterSec'] === 'number' ? (data['retryAfterSec'] as number) : undefined,
            };
        }
        // Optimistically update local state so the gate flips to "pending".
        const pending = typeof data['contactEmailPending'] === 'string' ? (data['contactEmailPending'] as string) : undefined;
        if (pending) this.contactEmailPending$.next(pending);
        return {
            ok: true,
            alreadyVerified: data['alreadyVerified'] === true,
            contactEmail: typeof data['contactEmail'] === 'string' ? (data['contactEmail'] as string) : undefined,
            contactEmailPending: pending,
            expiresAt: typeof data['expiresAt'] === 'string' ? (data['expiresAt'] as string) : undefined,
        };
    }

    async verifyToken(token: string): Promise<VerifyResult> {
        const res = await fetch('/api/verify-contact-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
        }).catch(() => null);
        if (!res) return { ok: false, reason: 'internal_error' };
        const data: Record<string, unknown> = await res.json().catch(() => ({}));
        if (!res.ok || data['ok'] !== true) {
            return {
                ok: false,
                reason: typeof data['reason'] === 'string' ? (data['reason'] as string) : `http_${res.status}`,
            };
        }
        return {
            ok: true,
            contactEmail: typeof data['contactEmail'] === 'string' ? (data['contactEmail'] as string) : undefined,
        };
    }
}
